import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Chip, CircularProgress, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LEVELS = [
  { value: '', label: 'Every level' },
  { value: 'enterprise', label: 'Enterprises' },
  { value: 'organization', label: 'Organisations' },
  { value: 'region', label: 'Regions' },
  { value: 'clinic', label: 'Offices' },
  { value: 'user', label: 'Individual clinicians' },
];

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Every movement of AI sessions on the platform, in one place.
 *
 * The ledger has always existed — a sale, a hand-down out of a pool, a take-back as a
 * negative — but it could only be read one clinic at a time, which answers a question
 * nobody asks. This answers the one people do: what has this customer been given, and
 * by whom.
 */
export default function CreditHistory() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (search, level) => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (level) params.set('scope', level);
      const { data: d } = await axios.get(
        `${API_URL}/api/v1/superadmin/credits/history?${params.toString()}`);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim(), scope), 300);   // a keystroke is not a request
    return () => clearTimeout(t);
  }, [q, scope, load]);

  const rows = data?.movements || [];

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: '"Fraunces", Georgia, serif', mb: 0.5 }}>
        Session history
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Every movement of AI sessions: what each customer was sold, what their
        administrators handed down to their own offices, and anything taken back. Search
        by name, or narrow to one level.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          size="small" value={q} onChange={(e) => setQ(e.target.value)} sx={{ flex: 1 }}
          placeholder="Search by customer, organisation or office name…"
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
        <TextField select size="small" label="Level" value={scope} sx={{ minWidth: 220 }}
          SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          onChange={(e) => setScope(e.target.value)}>
          {LEVELS.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
        </TextField>
      </Stack>

      {data && (
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" color="success" variant="outlined"
            label={`${data.total_given || 0} sessions given`} />
          <Chip size="small" color="error" variant="outlined"
            label={`${data.total_taken_back || 0} taken back`} />
          <Chip size="small" variant="outlined" label={`${rows.length} shown`} />
        </Stack>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : rows.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              No session movements match that.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Who received them</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell align="right">Sessions</TableCell>
                    <TableCell>Out of</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((m) => {
                    const n = m.amount_sessions;
                    const back = n !== null && n < 0;
                    const set = m.direction === 'set';
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(m.at)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{m.to_name}</TableCell>
                        <TableCell><Chip size="small" variant="outlined" label={m.to_label} /></TableCell>
                        <TableCell align="right">
                          {n === null || n === undefined
                            ? <Chip size="small" color="success" label="Unlimited" />
                            : set
                              // A set is the new total, not sessions added on top.
                              ? <Typography component="span" fontWeight={700}>Set to {n}</Typography>
                              : <Typography component="span" fontWeight={700}
                                  color={back ? 'error.main' : 'success.main'}>
                                  {back ? `− ${Math.abs(n)}` : `+ ${n}`}
                                </Typography>}
                        </TableCell>
                        {/* Where it came from: a parent's pool, or a sale by us. Those
                            are different events and the difference matters. */}
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {m.from_platform ? 'ScribeLive' : m.from_name}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{m.by || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
